<template>
  <div class="work-calendar">
    <div class="studio-cockpit-wrapper fill-height d-flex flex-column" :class="themeClasses" @dragend="onDragEndGlobal" @mouseup="onDragEndGlobal">
      <!-- Premium Header -->
      <div class="wc-header">
        <div class="wc-header-left">
          <v-btn icon small dark class="wc-back-btn mr-3" @click="$emit('back')">
            <v-icon small>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="wc-date-block">
            <div class="wc-day-name">{{ $moment(currentDate).format("dddd") }}</div>
            <div class="wc-full-date">{{ $moment(currentDate).format("DD. MMMM YYYY") }}</div>
          </div>
          <div v-if="isToday" class="wc-today-badge">Heute</div>
        </div>

        <div class="wc-header-center">
          <div class="wc-stat">
            <v-icon x-small color="green accent-3" class="mr-1">mdi-account-group</v-icon>
            <span>{{ viewColumns.length }} aktiv</span>
          </div>
        </div>

        <div class="wc-header-right">
          <div class="wc-nav-group">
            <v-btn icon x-small @click="changeDate(-1)" class="wc-nav-btn"><v-icon small>mdi-chevron-left</v-icon></v-btn>
            <v-btn x-small text class="wc-today-btn" :class="{ 'is-today': isToday }" @click="jumpToToday">Heute</v-btn>
            <v-btn icon x-small @click="changeDate(1)" class="wc-nav-btn"><v-icon small>mdi-chevron-right</v-icon></v-btn>
          </div>

          <v-menu offset-y :close-on-content-click="false" transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" class="wc-icon-btn"><v-icon small>mdi-calendar-search</v-icon></v-btn>
            </template>
            <v-date-picker v-model="currentDate" no-title scrollable dark color="green accent-3" @input="fetchData"></v-date-picker>
          </v-menu>

          <v-btn icon x-small @click="sidebarOpen = !sidebarOpen" class="wc-icon-btn" :class="{ active: sidebarOpen }">
            <v-icon small>{{ sidebarOpen ? "mdi-page-layout-sidebar-right" : "mdi-page-layout-sidebar-right-off" }}</v-icon>
          </v-btn>

          <v-btn icon x-small @click="isDarkMode = !isDarkMode" class="wc-icon-btn">
            <v-icon small>{{ isDarkMode ? "mdi-weather-night" : "mdi-white-balance-sunny" }}</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="flex-grow-1 d-flex overflow-hidden relative">
        <div class="flex-grow-1 d-flex flex-column relative" style="min-width: 0; border-right: 1px solid rgba(255, 255, 255, 0.04)">
          <div class="d-flex flex-grow-1 overflow-hidden">
            <!-- Time Axis -->
            <div
              class="time-axis d-flex flex-column custom-scrollbar"
              style="
                width: 56px;
                flex-shrink: 0;
                background: rgba(8, 8, 8, 0.95);
                overflow-y: hidden;
                border-right: 1px solid rgba(255, 255, 255, 0.04);
              "
              ref="timeAxis">
              <div :style="{ height: headerOffset + 'px' }" style="flex-shrink: 0"></div>
              <div v-for="hour in hours" :key="hour" class="time-slot-label relative" :style="{ height: hourHeight + 'px' }">
                <span class="wc-hour-label">{{ hour }}:00</span>
                <div class="wc-hour-tick"></div>
              </div>
            </div>

            <!-- Column Scroller -->
            <div class="wc-scroller custom-scrollbar" ref="scroller" @scroll="syncScroll">
              <!-- Loading Skeleton -->
              <template v-if="loading">
                <div v-for="n in 5" :key="'skel' + n" class="wc-skeleton-col">
                  <div class="wc-skeleton-header">
                    <div class="wc-skeleton-avatar wc-pulse"></div>
                    <div style="flex: 1">
                      <div class="wc-skeleton-line wc-pulse" style="width: 70%; height: 12px"></div>
                      <div class="wc-skeleton-line wc-pulse" style="width: 50%; height: 8px; margin-top: 6px"></div>
                    </div>
                  </div>
                  <div class="wc-skeleton-body">
                    <div
                      v-for="b in 3"
                      :key="'sb' + b"
                      class="wc-skeleton-block wc-pulse"
                      :style="{ top: 60 + b * 120 + 'px', height: 50 + Math.random() * 60 + 'px' }"></div>
                  </div>
                </div>
              </template>

              <!-- Current Time Line -->
              <div
                v-if="!loading && isToday && currentTimeTop > 0"
                class="current-time-line z-index-20"
                :style="{ top: currentTimeTop + headerOffset + 'px', left: 0, right: 0 }">
                <div class="time-badge">{{ $moment().format("HH:mm") }}</div>
              </div>

              <!-- Empty State -->
              <div
                v-if="!loading && viewColumns.length === 0"
                class="fill-height w-100 d-flex flex-column align-center justify-center"
                style="min-height: 400px">
                <div class="wc-empty-icon">
                  <v-icon size="40" style="color: rgba(255, 255, 255, 0.15)">mdi-calendar-blank-outline</v-icon>
                </div>
                <div style="color: rgba(255, 255, 255, 0.25); font-size: 0.95rem; font-weight: 600; margin-top: 16px">Keine Schichten geplant</div>
                <div style="color: rgba(255, 255, 255, 0.12); font-size: 0.8rem; margin-top: 4px">Erstelle einen Einsatzplan für diesen Tag</div>
              </div>

              <!-- Columns -->
              <template v-if="!loading">
                <work-calendar-column
                  v-for="col in viewColumns"
                  :key="col.id"
                  :col="col"
                  :hours-count="hours.length"
                  :hour-height="hourHeight"
                  :header-offset="headerOffset"
                  :hover-state="hoverState"
                  @drag-over="handleDragOver"
                  @drop="handleDrop"
                  @click-item="handleBlockClick"
                  @click-empty="handleEmptyClick"
                  @open-note="openNoteDialog" />
              </template>
            </div>
          </div>
        </div>

        <transition name="slide-x-reverse-transition">
          <work-calendar-sidebar v-if="sidebarOpen" :templates="templates" @create="openCreateTemplate" @drag-start="handleDragStart" />
        </transition>
      </div>

      <work-calendar-drop-dialog v-model="dropDialog" :data="pendingDrop" @save="confirmDrop" />

      <work-calendar-edit-dialog
        v-model="editDialog"
        :edit-data="editData"
        :customer-details="customerDetails"
        :loading="customerLoading"
        :employees="employeeList"
        :current-date="currentDate"
        @save="saveEdit"
        @delete="deleteItem"
        @action="triggerSpecialAction" />

      <v-dialog v-model="createTemplateDialog" max-width="480" persistent>
        <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
          <v-card-title class="white--text" style="font-size: 1rem">
            <v-icon left small color="green accent-3">mdi-plus-circle</v-icon>
            Neue Vorlage
          </v-card-title>
          <v-card-text class="pt-3">
            <v-row dense>
              <v-col cols="12">
                <v-text-field v-model="newTemplate.title" label="Titel" outlined dense dark autofocus hide-details class="mb-3"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-select v-model="newTemplate.type" :items="['appointment', 'task']" label="Typ" outlined dense dark hide-details>
                  <template v-slot:item="{ item }">{{ item === "appointment" ? "Termin" : "Ämtli" }}</template>
                </v-select>
              </v-col>
              <v-col cols="6" v-if="newTemplate.type === 'appointment'">
                <v-text-field v-model="newTemplate.duration" label="Dauer (Min)" type="number" outlined dense dark hide-details></v-text-field>
              </v-col>
              <v-col cols="12" class="mt-2">
                <div
                  class="text-caption mb-2"
                  style="color: rgba(255, 255, 255, 0.3); font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.5px">
                  Farbe
                </div>
                <div class="d-flex flex-wrap" style="gap: 6px">
                  <div
                    v-for="c in colorOptions"
                    :key="c"
                    class="wc-color-dot"
                    :class="{ active: newTemplate.color === c }"
                    :style="{ background: c }"
                    @click="newTemplate.color = c"></div>
                </div>
              </v-col>
              <v-col cols="12" class="mt-3">
                <div
                  class="text-caption mb-2"
                  style="color: rgba(255, 255, 255, 0.3); font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.5px">
                  Icon
                </div>
                <div class="d-flex flex-wrap custom-scrollbar" style="max-height: 120px; overflow-y: auto; gap: 4px">
                  <div
                    v-for="ic in iconOptions"
                    :key="ic.icon"
                    class="wc-icon-pick"
                    :class="{ active: newTemplate.icon === ic.icon }"
                    @click="newTemplate.icon = ic.icon">
                    <v-icon small :color="newTemplate.icon === ic.icon ? 'white' : 'rgba(255,255,255,0.3)'">{{ ic.icon }}</v-icon>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" v-if="newTemplate.type === 'appointment'">
                <v-switch v-model="newTemplate.requiresCustomer" label="Erfordert Kunde?" dense inset dark hide-details class="mt-2"></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-5 pb-4">
            <v-spacer></v-spacer>
            <v-btn text small @click="createTemplateDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Abbrechen</v-btn>
            <v-btn
              small
              depressed
              @click="saveTemplate"
              :disabled="!newTemplate.title"
              style="
                background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
                color: white !important;
                border-radius: 8px;
                text-transform: none;
                font-weight: 600;
              ">
              <v-icon left x-small>mdi-check</v-icon> Erstellen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="noteDialog" max-width="440">
        <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
          <v-card-title style="font-size: 1rem">
            <v-icon left small color="blue lighten-2">mdi-message-text</v-icon>
            Notizen · {{ selectedCol?.info.firstName }}
          </v-card-title>
          <v-card-text class="pt-2">
            <div v-if="selectedCol?.notes.length > 0" class="mb-3">
              <div
                v-for="note in selectedCol.notes"
                :key="note._id"
                class="d-flex align-start pa-3 mb-2 rounded-lg"
                style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.04)">
                <v-icon x-small color="blue lighten-2" class="mr-2 mt-1">mdi-message</v-icon>
                <div class="flex-grow-1">
                  <div class="white--text text-body-2" style="white-space: pre-wrap; font-size: 0.85rem">{{ note.content }}</div>
                  <div class="text-caption mt-1" style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem">
                    {{ note.author ? note.author.vorname : "Team" }} · {{ $moment(note.createdAt).format("HH:mm") }}
                  </div>
                </div>
                <v-btn icon x-small @click="deleteNote(note._id)" style="color: rgba(255, 255, 255, 0.2)"><v-icon x-small>mdi-close</v-icon></v-btn>
              </div>
            </div>
            <div v-else class="text-center py-4" style="color: rgba(255, 255, 255, 0.2); font-size: 0.8rem">Keine Notizen</div>
            <v-textarea
              v-model="newNoteContent"
              outlined
              dense
              label="Nachricht..."
              rows="2"
              hide-details
              dark
              class="mt-2"
              style="font-size: 0.85rem"></v-textarea>
          </v-card-text>
          <v-card-actions class="px-5 pb-4">
            <v-spacer></v-spacer>
            <v-btn text small @click="noteDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Schließen</v-btn>
            <v-btn
              small
              depressed
              @click="createNote"
              :disabled="!newNoteContent"
              style="
                background: linear-gradient(135deg, #42a5f5, #1565c0) !important;
                color: white !important;
                border-radius: 8px;
                text-transform: none;
                font-weight: 600;
              ">
              <v-icon left x-small>mdi-send</v-icon> Senden
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import moment from "moment"

// Import Sub-Components
import WorkCalendarColumn from "./WorkCalendarColumn"
import WorkCalendarSidebar from "./WorkCalendarSidebar"
import WorkCalendarDropDialog from "./WorkCalendarDropDialog"
import WorkCalendarEditDialog from "./WorkCalendarEditDialog"

import { mapActions } from "vuex"
export default {
  name: "WorkCalendar",
  components: {
    WorkCalendarColumn,
    WorkCalendarSidebar,
    WorkCalendarDropDialog,
    WorkCalendarEditDialog,
  },
  data() {
    return {
      isDarkMode: true,

      currentDate: moment().format("YYYY-MM-DD"),
      loading: false,
      customerLoading: false, // NEW: Loading state for customer details
      columns: [],
      viewColumns: [],
      templates: [],
      liveAttendanceMap: {},
      studioId: userService.getUser().studio._id,

      startHour: 6,
      endHour: 23,
      hourHeight: 100,
      headerOffset: 80,
      timerInterval: null,
      currentTimeTop: 0,
      sidebarOpen: true,
      hoverState: null,

      // Dialogs
      dropDialog: false,
      pendingDrop: null,
      editDialog: false,
      editData: {},
      customerDetails: null,
      createTemplateDialog: false,
      newTemplate: { title: "", type: "appointment", duration: 60, color: "#2196F3", icon: "mdi-calendar", requiresCustomer: false },
      noteDialog: false,
      selectedCol: null,
      newNoteContent: "",

      // Config Options
      iconOptions: [
        { name: "Training", icon: "mdi-dumbbell" },
        { name: "Plan", icon: "mdi-clipboard-list-outline" },
        { name: "Probetraining", icon: "mdi-account-star" },
        { name: "Check-Up", icon: "mdi-heart-pulse" },
        { name: "Ernährung", icon: "mdi-food-apple" },
        { name: "Kurs", icon: "mdi-yoga" },
        { name: "Verkauf", icon: "mdi-cash-register" },
        { name: "Meeting", icon: "mdi-account-group" },
        { name: "Büro", icon: "mdi-laptop" },
        { name: "Telefon", icon: "mdi-phone" },
        { name: "Reinigung", icon: "mdi-broom" },
        { name: "Wartung", icon: "mdi-tools" },
        { name: "Theke", icon: "mdi-coffee" },
        { name: "Rundgang", icon: "mdi-eye-check" },
        { name: "Müll", icon: "mdi-delete" },
        { name: "Inventar", icon: "mdi-package-variant" },
        { name: "Schliessen", icon: "mdi-door-closed-lock" },
        { name: "Öffnen", icon: "mdi-door-open" },
        { name: "Pause", icon: "mdi-pause-circle" },
        { name: "Event", icon: "mdi-party-popper" },
      ],
      colorOptions: ["#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", "#00BCD4", "#607D8B", "#E91E63"],
    }
  },
  computed: {
    employeeList() {
      return this.viewColumns.map((col) => ({
        id: col.id,
        name: col.firstName, // oder col.info.firstName + ' ' + col.info.lastName
      }))
    },
    themeClasses() {
      return this.isDarkMode ? "theme--dark" : "theme--light"
    },
    hours() {
      const h = []
      for (let i = this.startHour; i <= this.endHour; i++) h.push(i)
      return h
    },
    isToday() {
      return moment(this.currentDate).isSame(moment(), "day")
    },
  },
  mounted() {
    this.fetchData()
    this.updateTimeLine()
    this.fetchLiveStatus()
    this.startClock()
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
  },
  methods: {
    ...mapActions("panelStore", ["selectCustomer", "clearCustomer", "setCustomerInfos"]),

    async fetchData() {
      this.loading = true
      try {
        const res = await api.shiftManagement.fetchDailyView(this.currentDate)
        this.columns = res.data.columns || []
        this.templates = res.data.templates || []
        await this.fetchLiveStatus()
        this.processViewData()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
        this.$nextTick(() => {
          if (this.isToday) this.scrollToNow()
        })
      }
    },

    processViewData() {
      this.viewColumns = this.columns
        .filter((col) => col.shifts && col.shifts.length > 0) // FILTER: Nur mit Schicht
        .map((col) => ({
          id: col.info._id,
          firstName: col.info.firstName,
          picture: col.info.picture,
          info: col.info,
          shifts: col.shifts,
          tasks: col.tasks,
          appointments: col.appointments,

          shiftRange: this.getShiftRange(col),
          liveStatus: this.getLiveStatus(col),
          notes: col.notes,
          backgrounds: this.calculateBackgrounds(col),
          items: this.calculateItems(col),
        }))
    },

    // --- ITEM ACTIONS ---
    async handleBlockClick(block, col) {
      if (block.type === "system") return

      this.editData = {
        id: block._id,
        isTask: block.isTask,
        title: block.title,
        description: block.rawData.description,
        color: block.borderColor,
        start: block.start,
        end: block.end,
        colId: col.id,
        specialType: block.rawData.specialType,
        customer: block.rawData.customer,
        duration: block.end ? moment(block.end, "HH:mm").diff(moment(block.start, "HH:mm"), "minutes") : 30,
      }

      this.customerDetails = null

      // 🚀 FETCH EXTENDED CUSTOMER DATA (ASYNC)
      if (this.editData.customer) {
        this.customerLoading = true
        this.editDialog = true // Open dialog immediately

        try {
          // Fetch full pool entry
          const poolData = await api.fetchCustomerInfosAndUpdatePool({ roodyUserId: this.editData.customer._id })
          this.customerDetails = poolData

          // STORE SELECTION: Set selected customer globally if store exists
        } catch (e) {
          console.error("Customer info fetch failed", e)
        } finally {
          this.customerLoading = false
        }
      } else {
        this.editDialog = true
      }
    },

    // --- (Rest of the methods remain unchanged: isTimeSlotFree, handleDragOver, handleDrop, confirmDrop, saveEdit, deleteItem, triggerSpecialAction, etc.) ---
    isTimeSlotFree(col, startStr, endStr, excludeId = null) {
      const newStart = moment(startStr, "HH:mm")
      const newEnd = moment(endStr, "HH:mm")
      if (!col.backgrounds || !col.backgrounds.length) return "Mitarbeiter hat keine Schicht."
      let covered = false
      for (const bg of col.backgrounds) {
        const bgStart = moment(bg.start, "HH:mm")
        const bgEnd = moment(bg.end, "HH:mm")
        if (newStart.isSameOrAfter(bgStart) && newEnd.isSameOrBefore(bgEnd)) {
          covered = true
          break
        }
      }
      if (!covered) return "Außerhalb der Arbeitszeit."
      for (const item of col.items) {
        if (item._id === excludeId) continue
        const itemStart = moment(item.start, "HH:mm")
        let itemEndStr = item.end || moment(item.start, "HH:mm").add(30, "minutes").format("HH:mm")
        const itemEnd = moment(itemEndStr, "HH:mm")
        if (newStart.isBefore(itemEnd) && newEnd.isAfter(itemStart)) return `Kollision mit "${item.title}"`
      }
      return true
    },
    handleDragStart(tpl) {
      /* ... */
    },
    handleDragOver({ event, colId, relativeY }) {
      const hoursClicked = Math.max(0, relativeY / this.hourHeight)
      const totalMin = hoursClicked * 60
      const hour = this.startHour + Math.floor(totalMin / 60)
      const min = Math.round((totalMin % 60) / 15) * 15
      const timeStr = moment().hour(hour).minute(min).format("HH:mm")
      const topPx = (Math.max(0, (hour - this.startHour) * 60 + min) / 60) * this.hourHeight
      const col = this.viewColumns.find((c) => c.id === colId)
      const endTime = moment(timeStr, "HH:mm").add(60, "minutes").format("HH:mm")
      const validation = this.isTimeSlotFree(col, timeStr, endTime)
      const error = validation === true ? null : validation

      if (this.hoverState && this.hoverState.colId === colId && this.hoverState.time === timeStr) {
        return
      }
      this.hoverState = { colId: colId, time: timeStr, top: topPx, height: this.hourHeight, error }
    },
    onDragEndGlobal() {
      this.hoverState = null
    },
    handleDrop({ event, col }) {
      this.hoverState = null
      const tplData = event.dataTransfer.getData("templateObj")
      if (!tplData) return
      const template = JSON.parse(tplData)
      const rect = event.currentTarget.querySelector(".lane-body").getBoundingClientRect()
      const relativeY = event.clientY - rect.top
      const totalMin = Math.max(0, relativeY / this.hourHeight) * 60
      const hour = this.startHour + Math.floor(totalMin / 60)
      const min = Math.round((totalMin % 60) / 15) * 15
      const timeStr = moment().hour(hour).minute(min).format("HH:mm")
      const duration = template.duration || 60
      const endM = moment(timeStr, "HH:mm").add(duration, "minutes")
      const valid = this.isTimeSlotFree(col, timeStr, endM.format("HH:mm"))
      if (valid !== true) {
        this.$toast.error(valid)
        return
      }
      this.pendingDrop = { template: { ...template }, col, time: timeStr, description: "" }
      this.dropDialog = true
    },
    async confirmDrop(data) {
      const { template, col, time, duration, description, customer } = data
      const endM = moment(`${this.currentDate} ${time}`, "YYYY-MM-DD HH:mm").add(duration, "minutes")
      const valid = this.isTimeSlotFree(col, time, endM.format("HH:mm"))
      if (valid !== true) {
        this.$toast.error(valid)
        return
      }
      try {
        if (template.type === "task") {
          await api.shiftManagement.createTask({
            date: this.currentDate,
            time,
            title: template.title,
            description,
            assignedEmployee: col.info._id,
            icon: template.icon,
            color: template.color,
          })
        } else {
          await api.shiftManagement.createAppointment({
            date: this.currentDate,
            start: time,
            end: endM.format("HH:mm"),
            title: template.title,
            description,
            assignedEmployee: col.info._id,
            customer: customer ? customer : null,
            icon: template.icon,
            color: template.color,
            specialType: template.specialType || null,
          })
        }
        this.$toast.success("Erstellt!")
        this.dropDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
      }
    },
    async saveEdit(data) {
      // Validierung (Zeitraum prüfen)
      // ACHTUNG: Wir müssen jetzt in der Spalte prüfen, in die verschoben wurde (data.colId), nicht nur in der alten!
      const targetCol = this.viewColumns.find((c) => c.id === data.colId)

      let endStr = data.end
      if (!data.isTask) endStr = moment(data.start, "HH:mm").add(data.duration, "minutes").format("HH:mm")

      // Wenn Datum geändert wurde, ist es komplexer mit isTimeSlotFree (da das nur für den aktuellen Tag prüft).
      // Fürs erste nehmen wir an, der Backend-Call validiert oder wir prüfen nur, wenn es heute ist.
      if (data.date === this.currentDate) {
        const valid = this.isTimeSlotFree(targetCol, data.start, endStr, data.id)
        if (valid !== true) {
          this.$toast.error(valid)
          return
        }
      }

      try {
        if (data.isTask) {
          // Task Update Logik...
        } else {
          // TERMIN UPDATE
          // Wir senden jetzt auch assignedEmployee und date mit
          const payload = {
            title: data.title,
            description: data.description,
            color: data.color,
            start: data.start,
            end: endStr,
            status: data.status, // Dein neuer Status
            assignedEmployee: data.colId, // WICHTIG: Neuer Mitarbeiter
            date: data.date, // WICHTIG: Neues Datum
          }

          await api.shiftManagement.updateAppointment(data.id, payload)
        }

        this.$toast.success("Gespeichert")
        this.editDialog = false

        // Wenn das Datum geändert wurde, müssen wir evtl. zu dem Tag springen oder einfach neu laden
        if (data.date !== this.currentDate) {
          const switchDate = confirm("Termin wurde auf einen anderen Tag verschoben. Zu diesem Tag springen?")
          if (switchDate) {
            this.currentDate = data.date
          }
        }

        this.fetchData() // Refresh
      } catch (e) {
        console.log(e)
        this.$toast.error("Fehler beim Speichern")
      }
    },
    async deleteItem(data) {
      if (!confirm("Wirklich löschen?")) return
      try {
        if (data.isTask) await api.shiftManagement.deleteTask(data.id)
        else await api.shiftManagement.deleteAppointment(data.id)
        this.editDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    triggerSpecialAction(data) {
      const type = data.specialType
      const cid = data.customer._id
      if (type === "workout") this.$router.push(`/workouts/createnew?userId=${cid}`)
      else if (type === "anamnese") this.$router.push(`/dashboard/user/search/${cid}`)
      else if (type === "upgrade") this.$router.push(`/dashboard/contract?userId=${cid}&upgrade=true`)
    },
    openCreateTemplate() {
      this.newTemplate = { title: "", type: "appointment", duration: 60, color: "#2196F3", icon: "mdi-calendar", requiresCustomer: false }
      this.createTemplateDialog = true
    },
    async saveTemplate() {
      try {
        await api.shiftManagement.createTemplate(this.newTemplate)
        this.$toast.success("Vorlage gespeichert")
        this.createTemplateDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    openNoteDialog(col) {
      this.selectedCol = col
      this.newNoteContent = ""
      this.noteDialog = true
    },
    async createNote() {
      try {
        await api.shiftManagement.createNote({ date: this.currentDate, targetEmployee: this.selectedCol.info._id, content: this.newNoteContent })
        this.$toast.success("Gesendet")
        this.newNoteContent = ""
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteNote(id) {
      if (!confirm("Löschen?")) return
      try {
        await api.shiftManagement.deleteNote(id)
        this.fetchData()
      } catch (e) {}
    },
    handleEmptyClick(e, col) {
      this.$toast.info("Nutze die Vorlagen rechts!")
    },
    getTopFromTime(timeStr) {
      if (!timeStr) return 0
      const [h, m] = timeStr.split(":").map(Number)
      return (((h - this.startHour) * 60 + m) / 60) * this.hourHeight
    },
    getTop(hour, minute = 0) {
      return (((hour - this.startHour) * 60 + minute) / 60) * this.hourHeight
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(100,100,100, ${alpha})`
      if (hex.startsWith("#")) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return hex
    },
    getShiftRange(col) {
      if (!col.shifts.length) return "-"
      let all = []
      col.shifts.forEach((s) => {
        if (s.times) all.push(...s.times)
      })
      if (!all.length) return "-"
      all.sort((a, b) => a.start.localeCompare(b.start))
      return `${all[0].start} - ${all[all.length - 1].end}`
    },
    calculateBackgrounds(col) {
      return col.shifts
        .map((s) => {
          if (!s.times || !s.times.length) return null
          const sorted = [...s.times].sort((a, b) => a.start.localeCompare(b.start))
          const top = this.getTopFromTime(sorted[0].start)
          const bottom = this.getTopFromTime(sorted[sorted.length - 1].end)
          const color = s.shiftTemplate?.color || "#333"
          return {
            top,
            height: bottom - top,
            color: color,
            bgColor: this.hexToRgba(color, 0.1),
            start: sorted[0].start,
            end: sorted[sorted.length - 1].end,
          }
        })
        .filter((x) => x)
    },
    calculateItems(col) {
      let items = []
      col.shifts.forEach((s) => {
        const times = s.times || []
        if (!times.length) return
        times.forEach((t) => {
          if (t.type === "Pause")
            items.push({
              title: "Pause",
              start: t.start,
              end: t.end,
              top: this.getTopFromTime(t.start),
              height: this.getTopFromTime(t.end) - this.getTopFromTime(t.start),
              class: "item-pause", // Spezielle Klasse für Schraffur bleibt
              borderColor: "#FF9800",
              icon: "mdi-coffee",
              iconColor: "orange",
              type: "system",
            })
        })
        if (s.shiftTemplate) {
          if (s.shiftTemplate.isOpeningShift) {
            const sorted = [...times].sort((a, b) => a.start.localeCompare(b.start))
            if (sorted.length > 0) {
              const startStr = sorted[0].start
              const endStr = moment(startStr, "HH:mm").add(30, "minutes").format("HH:mm")
              const top = this.getTopFromTime(startStr)
              items.push({
                title: "Centeröffnung",
                subtitle: "Fix",
                start: startStr,
                end: endStr,
                top,
                height: this.getTopFromTime(endStr) - top,
                class: "item-system glass-red",
                borderColor: "#D32F2F",
                icon: "mdi-door-open",
                iconColor: "red lighten-3",
                type: "system",
              })
            }
          }
          if (s.shiftTemplate.isClosingShift) {
            const sorted = [...times].sort((a, b) => a.start.localeCompare(b.start))
            if (sorted.length > 0) {
              const endStr = sorted[sorted.length - 1].end
              const startStr = moment(endStr, "HH:mm").subtract(30, "minutes").format("HH:mm")
              const top = this.getTopFromTime(startStr)
              items.push({
                title: "Centerschliessung",
                subtitle: "Fix",
                start: startStr,
                end: endStr,
                top,
                height: this.getTopFromTime(endStr) - top,
                class: "item-system glass-red",
                borderColor: "#D32F2F",
                icon: "mdi-door-closed",
                iconColor: "red lighten-3",
                type: "system",
              })
            }
          }
        }
      })
      col.tasks.forEach((t) => {
        const color = t.color || "#9C27B0" // Default Lila
        items.push({
          _id: t._id,
          title: t.title,
          subtitle: t.description || "Aufgabe",
          start: t.time,
          end: "",
          top: this.getTopFromTime(t.time),
          height: (30 / 60) * this.hourHeight,

          // HIER IST DIE ÄNDERUNG: Kein 'class': 'glass-purple' mehr!
          // Stattdessen direkter Style:
          style: {
            background: `linear-gradient(135deg, ${this.hexToRgba(color, 0.35)} 0%, ${this.hexToRgba(color, 0.15)} 100%)`,
            border: `1px solid ${this.hexToRgba(color, 0.5)}`,
            boxShadow: `0 4px 15px ${this.hexToRgba(color, 0.15)}`,
            backdropFilter: "blur(4px)",
          },

          borderColor: color, // Für den linken Rand (optional)
          icon: t.icon || "mdi-checkbox-marked",
          iconColor: "white",
          isTask: true,
          rawData: t,
        })
      })

      // 3. Appointments (Termine) - Jetzt dynamisch!
      col.appointments.forEach((a) => {
        const top = this.getTopFromTime(a.start)
        const color = a.color || "#2196F3" // Default Blau

        items.push({
          _id: a._id,
          title: a.title,
          subtitle: a.customer ? `${a.customer.vorname} ${a.customer.nachname}` : a.description,
          start: a.start,
          end: a.end,
          top,
          height: this.getTopFromTime(a.end) - top,

          // HIER IST DIE ÄNDERUNG: Dynamischer Glass-Effekt basierend auf Termin-Farbe
          style: {
            background: `linear-gradient(135deg, ${this.hexToRgba(color, 0.35)} 0%, ${this.hexToRgba(color, 0.15)} 100%)`,
            border: `1px solid ${this.hexToRgba(color, 0.5)}`,
            boxShadow: `0 4px 15px ${this.hexToRgba(color, 0.15)}`,
            backdropFilter: "blur(4px)",
          },

          borderColor: color,
          icon: a.icon || "mdi-calendar",
          iconColor: "white",
          isAppt: true,
          rawData: a,
          status: a.status || "planned", // Status durchreichen!
        })
      })
      return items
    },
    getLiveStatus(col) {
      return this.liveAttendanceMap[col.info.employeeId]
        ? { active: true, color: "green accent-4", since: this.liveAttendanceMap[col.info.employeeId].checkin || "Jetzt" }
        : { active: false, color: "grey darken-3", since: "" }
    },
    changeDate(d) {
      this.currentDate = moment(this.currentDate).add(d, "days").format("YYYY-MM-DD")
      this.fetchData()
    },
    jumpToToday() {
      this.currentDate = moment().format("YYYY-MM-DD")
      this.fetchData()
    },
    syncScroll(e) {
      if (this.$refs.timeAxis) this.$refs.timeAxis.scrollTop = e.target.scrollTop
    },
    scrollToNow() {
      if (!this.$refs.scroller) return
      const nowMinutes = moment().hours() * 60 + moment().minutes()
      const offset = Math.max(0, nowMinutes - this.startHour * 60 - 60)
      this.$refs.scroller.scrollTop = (offset / 60) * this.hourHeight
    },
    startClock() {
      this.timerInterval = setInterval(() => {
        this.updateTimeLine()
        this.fetchLiveStatus()
      }, 30000)
    },
    updateTimeLine() {
      console.log(this.currentTimeTop)
      if (!this.isToday) {
        this.currentTimeTop = -100
        return
      }
      const now = moment()
      const h = now.hour()
      if (h < this.startHour || h > this.endHour) {
        this.currentTimeTop = -100
        return
      }
      this.currentTimeTop = this.getTop(h, now.minute())
    },
    async fetchLiveStatus() {
      try {
        let data = await api.shiftManagement.fetchAttendanceTrainers(this.studioId)
        const map = {}
        data.forEach((w) => {
          map[w._id] = w
        })
        this.liveAttendanceMap = map
      } catch (e) {}
    },
  },
}
</script>

<style scoped>
::v-deep .studio-cockpit-wrapper {
  --bg-main: #0a0a0a;
  --bg-panel: rgba(255, 255, 255, 0.05);
  --bg-panel-dark: rgba(0, 0, 0, 0.3);
  --bg-card: #141414;
  --bg-sidebar: rgba(0, 0, 0, 0.2);
  --bg-input: #2a2a2a;
  --text-main: #ffffff;
  --text-secondary: #aaaaaa;
  --border-color: rgba(255, 255, 255, 0.06);
  --color-primary: #4caf50;
  --color-secondary: #1565c0;
  background: var(--bg-main) !important;
  color: var(--text-main);
  transition: background 0.3s ease, color 0.3s ease;
}
.v-input input,
.v-select__selection {
  color: white !important;
}
.v-icon {
  color: rgba(255, 255, 255, 0.7);
}

::v-deep .studio-cockpit-wrapper.theme--light {
  --bg-main: #f4f6f8;
  --bg-panel: #ffffff;
  --bg-panel-dark: #eeeeee;
  --bg-card: #ffffff;
  --bg-sidebar: #f9fafb;
  --bg-input: #ffffff;
  --text-main: #1a1a1a;
  --text-secondary: #616161;
  --border-color: #e0e0e0;
}

/* ===== PREMIUM HEADER ===== */
.wc-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid var(--border-color);
  z-index: 20;
}
.wc-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.wc-back-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  border-radius: 8px !important;
}
.wc-date-block {
  display: flex;
  flex-direction: column;
}
.wc-day-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  line-height: 1.2;
  text-transform: capitalize;
}
.wc-full-date {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 500;
}
.wc-today-badge {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: white;
}
.wc-header-center {
  display: flex;
  align-items: center;
  gap: 16px;
}
.wc-stat {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
}
.wc-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}
.wc-nav-group {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 2px;
  margin-right: 4px;
}
.wc-nav-btn {
  color: rgba(255, 255, 255, 0.5) !important;
}
.wc-nav-btn:hover {
  color: white !important;
}
.wc-today-btn {
  color: rgba(255, 255, 255, 0.6) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.7rem !important;
  min-width: 0 !important;
  padding: 0 8px !important;
  border-radius: 6px !important;
}
.wc-today-btn.is-today {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #66bb6a !important;
}
.wc-icon-btn {
  color: rgba(255, 255, 255, 0.35) !important;
  transition: all 0.2s;
}
.wc-icon-btn:hover,
.wc-icon-btn.active {
  color: white !important;
  background: rgba(255, 255, 255, 0.06) !important;
}

/* ===== EXISTING UTILITIES ===== */
.glass-panel,
.glass-btn,
.glass-panel-light {
  background: var(--bg-panel) !important;
  border: 1px solid var(--border-color) !important;
}
.glass-purple {
  background: linear-gradient(135deg, rgba(0, 150, 63, 0.15), rgba(0, 150, 63, 0.05));
  border: 1px solid rgba(0, 150, 63, 0.3);
}
.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}
.time-slot-label {
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}
.wc-hour-label {
  position: absolute;
  top: -7px;
  right: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.25);
  font-variant-numeric: tabular-nums;
}
.wc-hour-tick {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}

/* ===== CURRENT TIME LINE ===== */
.current-time-line {
  position: absolute;
  height: 2px;
  background: linear-gradient(90deg, #ff5252, transparent);
  pointer-events: none;
  z-index: 20;
  box-shadow: 0 0 12px rgba(255, 82, 82, 0.6);
}
.time-badge {
  position: absolute;
  left: -52px;
  top: -10px;
  background: #ff5252;
  color: white;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.4);
}

/* ===== SCROLLBAR ===== */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Scroller for columns */
.wc-scroller {
  display: flex;
  flex: 1 1 auto;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
}

/* iPad & Touch */
@media (max-width: 1024px) {
  .wc-header {
    padding: 8px 12px;
  }
  .wc-day-name {
    font-size: 1.2rem;
  }
  .wc-header-center {
    display: none;
  }
}

@media (hover: none) and (pointer: coarse) {
  .wc-scroller {
    -webkit-overflow-scrolling: touch;
  }
  .employee-column {
    min-width: 200px !important;
  }
}

/* Template Dialog */
.wc-color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.5;
  border: 2px solid transparent;
  transition: all 0.15s;
}
.wc-color-dot:hover {
  opacity: 0.8;
  transform: scale(1.1);
}
.wc-color-dot.active {
  opacity: 1;
  transform: scale(1.2);
  border-color: white;
  box-shadow: 0 0 10px currentColor;
}
.wc-icon-pick {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  transition: all 0.15s;
}
.wc-icon-pick:hover {
  background: rgba(255, 255, 255, 0.06);
}
.wc-icon-pick.active {
  background: rgba(76, 175, 80, 0.15);
  border-color: #4caf50;
}

/* ===== LOADING SKELETON ===== */
.wc-skeleton-col {
  min-width: 240px;
  flex: 1 1 240px;
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
}
.wc-skeleton-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(12, 12, 12, 0.98);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.wc-skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}
.wc-skeleton-line {
  border-radius: 4px;
}
.wc-skeleton-body {
  position: relative;
  height: 600px;
}
.wc-skeleton-block {
  position: absolute;
  left: 8px;
  right: 8px;
  border-radius: 10px;
}

/* Pulse Animation */
.wc-pulse {
  background: rgba(255, 255, 255, 0.04);
  animation: wcPulse 1.5s ease-in-out infinite;
}
@keyframes wcPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Empty State */
.wc-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
